"use client";

import React from "react";
import RevealElement from "../revealElement";
import AnimatedTextDesc from "../ui/animatedTextDesc";
import { visibleStatText } from "../ui/formatStatValue";
import BottomCurves from "../curves/bottomCurves";

type ResultCell = {
    textOnly: boolean;
    stat: string;
    label: string | null;
};

function toResultCell(heading: unknown, description: unknown): ResultCell | null {
    const stat = visibleStatText(heading);
    const label = visibleStatText(description);

    if (stat && /\d/.test(stat)) {
        return { textOnly: false, stat, label };
    }

    if (stat) {
        return { textOnly: true, stat, label };
    }

    if (label) {
        return { textOnly: true, stat: label, label: null };
    }

    return null;
}

export default function WorkResult({ data }: any) {
    const cells: ResultCell[] = (
        Array.isArray(data.results) ? data.results : []
    ).flatMap(
        (result: any) => {
            const cell = toResultCell(result?.heading, result?.description);
            return cell ? [cell] : [];
        }
    );

    const totalData = cells.length;
    const isThreeCol = totalData > 4 || totalData === 3;
    const hasTwoLastRow = isThreeCol && totalData % 3 === 2;
    const hasOneLastRow = isThreeCol && totalData % 3 === 1;
    const centerMobileLast = isThreeCol && totalData % 2 === 1;

    return (
        <section className="pt-20 pb-20 md:pb-52 bg-lightGrey ">
            <div className="flex flex-col items-center justify-center">
                <RevealElement className="w-full min-w-0">
                    <div className="text-center">
                        <h2 className="text-4xl text-darkGrey font-bold">
                            {data.heading}
                        </h2>
                        <p>{data.description}</p>
                    </div>

                    <div
                        className={`grid w-full min-w-0 grid-cols-2 items-start gap-x-6 gap-y-8 px-4 pt-6 [&>*]:min-w-0 md:px-6 ${
                            isThreeCol
                                ? `md:grid-cols-6 md:[&>*]:col-span-2 md:gap-x-8 ${
                                      hasTwoLastRow
                                          ? "md:[&>*:nth-last-child(2)]:col-start-2 md:[&>*:last-child]:col-start-4"
                                          : ""
                                  } ${
                                      hasOneLastRow
                                          ? "md:[&>*:last-child]:col-start-2"
                                          : ""
                                  } ${
                                      centerMobileLast
                                          ? "max-md:[&>*:last-child]:col-start-1 max-md:[&>*:last-child]:col-end-3 max-md:[&>*:last-child]:w-[calc(50%-0.75rem)] max-md:[&>*:last-child]:justify-self-center"
                                          : ""
                                  }`
                                : "mx-auto max-w-4xl md:gap-x-8"
                        }`}
                    >
                        {cells.map((cell, index) => (
                            <AnimatedTextDesc
                                key={index}
                                result={cell.stat}
                                description={cell.label}
                                textOnly={cell.textOnly}
                            />
                        ))}
                    </div>
                </RevealElement>
            </div>
            <BottomCurves
                direction="left"
                color={data.waveBgColor.waveBgColor}
            />
        </section>
    );
}
